import type {
  RaidBossMetricParse,
  RaidBossParse,
  RaidParseSummary,
  RaidSpecParses,
  RecentLog,
} from '@moussenaires/shared';
import { bestRaidParseSummary, formatDateTime, WCL_DIFFICULTY_IDS } from '@moussenaires/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandIcon } from './BrandIcon.js';
import { SegmentedControl } from './SegmentedControl.js';
import './CharacterRaidPanel.css';

const DIFFICULTY_ORDER = Object.keys(WCL_DIFFICULTY_IDS) as (keyof typeof WCL_DIFFICULTY_IDS)[];

/** Not a real spec name Warcraft Logs would ever report — safe to use as the synthetic "best of every spec" option's key. */
const BEST_SPEC_KEY = '__best__';

/** "78 (45)" — best (median), or an em dash when the character never logged this metric on this boss. */
function formatMetric(metric: RaidBossMetricParse | undefined): string {
  if (!metric || metric.bestPercent === null) return '—';
  return metric.medianPercent === null
    ? `${metric.bestPercent}`
    : `${metric.bestPercent} (${metric.medianPercent})`;
}

/**
 * Merges every spec's per-boss parses into one table: for each boss, each
 * metric (dps/hps/boss) independently keeps whichever spec's `bestPercent`
 * was higher — so a boss killed under two specs shows its single best
 * number regardless of which spec produced it, rather than favoring one
 * spec's numbers wholesale. Kills are summed across specs (the real total
 * number of times this boss was killed, spec-agnostic).
 */
function mergeBestAcrossSpecs(specs: RaidSpecParses[]): RaidBossParse[] {
  const byBoss = new Map<number, RaidBossParse>();
  for (const specEntry of specs) {
    for (const boss of specEntry.bosses) {
      const existing = byBoss.get(boss.encounterId);
      if (!existing) {
        byBoss.set(boss.encounterId, { ...boss, metrics: { ...boss.metrics } });
        continue;
      }
      const metrics: RaidBossParse['metrics'] = { ...existing.metrics };
      for (const key of ['dps', 'hps', 'boss'] as const) {
        const current = existing.metrics[key];
        const candidate = boss.metrics[key];
        if (!candidate) continue;
        if (!current || (candidate.bestPercent ?? -1) > (current.bestPercent ?? -1)) {
          metrics[key] = candidate;
        }
      }
      const kills =
        existing.kills === null && boss.kills === null
          ? null
          : (existing.kills ?? 0) + (boss.kills ?? 0);
      byBoss.set(boss.encounterId, { ...existing, metrics, kills });
    }
  }
  return [...byBoss.values()];
}

/**
 * A difficulty selector (only difficulties the character actually has
 * parses for), a spec selector underneath it (only shown when Warcraft Logs
 * recorded kills under more than one spec this tier), the per-boss table
 * for whichever combination is picked, and the guild's most recent logs
 * featuring this character below that.
 */
export function CharacterRaidPanel({
  raidParses,
  raidParsesError,
  recentLogs,
  recentLogsError,
}: {
  raidParses: RaidParseSummary[] | null;
  raidParsesError?: string;
  recentLogs: RecentLog[] | null;
  recentLogsError?: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as 'fr' | 'en';

  return (
    <div className="character-raid-panel">
      {!raidParses || raidParses.length === 0 ? (
        <p className="character-enrichment-unavailable">
          {raidParses ? t('roster.noRaidParses') : (raidParsesError ?? t('roster.notConfigured'))}
        </p>
      ) : (
        <RaidParseTables summaries={raidParses} />
      )}

      <h3 className="character-detail-section-title">{t('roster.recentLogs')}</h3>
      {recentLogs && recentLogs.length > 0 ? (
        <ul className="character-detail-logs">
          {recentLogs.map((log) => (
            <li key={log.code}>
              <a href={log.url} target="_blank" rel="noreferrer">
                {log.title}
                {log.zoneName ? ` — ${log.zoneName}` : ''}
              </a>
              <span className="character-detail-log-date">
                {formatDateTime(log.startedAt, locale)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="character-enrichment-unavailable">
          {recentLogs ? t('roster.noRecentLogs') : (recentLogsError ?? t('roster.notConfigured'))}
        </p>
      )}
    </div>
  );
}

function RaidParseTables({ summaries }: { summaries: RaidParseSummary[] }) {
  const { t } = useTranslation();
  const difficulties = DIFFICULTY_ORDER.filter((difficulty) =>
    summaries.some((summary) => summary.difficulty === difficulty),
  );
  const [difficulty, setDifficulty] = useState(
    () => bestRaidParseSummary(summaries)?.difficulty ?? difficulties[0]!,
  );
  const summary = summaries.find((entry) => entry.difficulty === difficulty) ?? summaries[0]!;
  // Defends against stale cached enrichment rows synced before `specs`
  // replaced the old flat `spec`/`bosses` shape — resyncing fixes the data,
  // this just keeps the panel from crashing meanwhile.
  const specs = summary.specs ?? [];

  // More than one spec: offer a synthetic "Best" option first (each boss's
  // best number regardless of which spec produced it), then every real spec
  // — defaulting to "Best". Exactly one spec: no selector, just its bosses.
  const hasMultipleSpecs = specs.length > 1;
  const [selectedSpec, setSelectedSpec] = useState<string | null>(null);
  const activeSpec =
    selectedSpec &&
    (selectedSpec === BEST_SPEC_KEY || specs.some((entry) => entry.spec === selectedSpec))
      ? selectedSpec
      : hasMultipleSpecs
        ? BEST_SPEC_KEY
        : (specs[0]?.spec ?? null);
  const bosses =
    activeSpec === BEST_SPEC_KEY
      ? mergeBestAcrossSpecs(specs)
      : (specs.find((entry) => entry.spec === activeSpec)?.bosses ?? []);

  return (
    <>
      {difficulties.length > 1 && (
        <SegmentedControl
          className="character-detail-selector"
          options={difficulties.map((value) => ({ value, label: t(`roster.difficulty.${value}`) }))}
          value={difficulty}
          onChange={setDifficulty}
          ariaLabel={t('roster.chooseDifficulty')}
        />
      )}
      {hasMultipleSpecs && (
        <SegmentedControl
          className="character-detail-selector"
          options={[
            { value: BEST_SPEC_KEY, label: t('roster.bestSpec') },
            ...specs.map((entry) => ({ value: entry.spec, label: entry.spec })),
          ]}
          value={activeSpec ?? ''}
          onChange={setSelectedSpec}
          ariaLabel={t('roster.spec')}
        />
      )}

      {(summary.overallBestPercent !== null || summary.overallMedianPercent !== null) && (
        <p className="raid-parse-overall">
          {summary.overallBestPercent ?? '—'}
          {summary.overallMedianPercent !== null && ` (${summary.overallMedianPercent})`}
        </p>
      )}

      <div className="raid-parse-table-wrap">
        <table className="raid-parse-table">
          <thead>
            <tr>
              <th>{t('roster.boss')}</th>
              <th>{t('roster.metricDps')}</th>
              <th>{t('roster.metricBoss')}</th>
              <th>{t('roster.metricHps')}</th>
              <th>{t('roster.kills')}</th>
            </tr>
          </thead>
          <tbody>
            {bosses.map((boss) => (
              <tr key={boss.encounterId}>
                <td>{boss.name}</td>
                <td>{formatMetric(boss.metrics.dps)}</td>
                <td>{formatMetric(boss.metrics.boss)}</td>
                <td>{formatMetric(boss.metrics.hps)}</td>
                <td>{boss.kills ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {summary.profileUrl && (
        <a
          href={summary.profileUrl}
          target="_blank"
          rel="noreferrer"
          className="character-enrichment-link"
        >
          <BrandIcon site="warcraftlogs" size={13} /> {t('roster.viewOnWarcraftLogs')}
        </a>
      )}
    </>
  );
}
